import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Key,
  Globe,
  HardDrive,
  UserCheck,
  CheckCircle2,
  Copy,
  Check,
  Eye,
  EyeOff,
  Sparkles,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { apiClient } from '../../services/api-client';
import { useAuth } from '../../context/AuthContext';
import type { MailboxResponse } from '@antigravity/types';

interface CreateMailboxModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (mailbox: MailboxResponse) => void;
}

export const CreateMailboxModal: React.FC<CreateMailboxModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { currentOrg } = useAuth();
  const currentOrgId = currentOrg?.id || 'a0000000-0000-0000-0000-000000000001';

  // Form states
  const [localPart, setLocalPart] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [customDomainName, setCustomDomainName] = useState('');
  const [isAddingNewDomain, setIsAddingNewDomain] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [quotaGigabytes, setQuotaGigabytes] = useState(10);
  const [createDedicatedUser, setCreateDedicatedUser] = useState(true);

  // Domains state
  const [domains, setDomains] = useState<any[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);

  // Submission & Success state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdResult, setCreatedResult] = useState<{
    mailbox: MailboxResponse;
    initialPassword?: string;
    dedicatedUserCreated: boolean;
  } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Load sending domains
  useEffect(() => {
    if (!isOpen) {
      setCreatedResult(null);
      setError(null);
      return;
    }

    const loadDomains = async () => {
      setIsLoadingDomains(true);
      try {
        const domainList = await apiClient.listSendingDomains().catch(() => []);
        setDomains(domainList);

        if (domainList.length > 0) {
          const verified = domainList.find((d: any) => d.status === 'VERIFIED' || d.status === 'ACTIVE');
          setSelectedDomainId(verified ? verified.id : domainList[0].id);
        } else {
          setIsAddingNewDomain(true);
        }
      } catch (err: any) {
        console.error('Failed to load domains', err);
      } finally {
        setIsLoadingDomains(false);
      }
    };

    loadDomains();
  }, [isOpen]);

  // Generate a random secure password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
    let result = 'Ag!';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    result += '9#';
    setPassword(result);
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const selectedDomain = domains.find((d) => d.id === selectedDomainId);
  const activeDomainName = isAddingNewDomain
    ? customDomainName.trim().toLowerCase()
    : selectedDomain?.domain || 'axiino.com';
  const fullEmail = localPart.trim() ? `${localPart.trim().toLowerCase()}@${activeDomainName}` : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanLocalPart = localPart.trim().toLowerCase();
    if (!cleanLocalPart) {
      setError('Please provide a valid mailbox username (local part).');
      return;
    }

    if (!/^[a-zA-Z0-9._-]+$/.test(cleanLocalPart)) {
      setError('Username can only contain alphanumeric characters, dots, underscores, or hyphens.');
      return;
    }

    let targetDomainId = selectedDomainId;

    setIsSubmitting(true);
    try {
      // 1. If adding a new domain, create domain first
      if (isAddingNewDomain) {
        const cleanDomain = customDomainName.trim().toLowerCase();
        if (!cleanDomain || !cleanDomain.includes('.')) {
          throw new Error('Please enter a valid domain name (e.g. yourcompany.com)');
        }
        const newDomain = await apiClient.createSendingDomain(cleanDomain);
        targetDomainId = newDomain.id;
      }

      if (!targetDomainId) {
        throw new Error('Please select or configure a sending domain for this mailbox.');
      }

      // 2. Ensure password exists (or generate one)
      const finalPassword = password.trim() || (() => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
        let res = 'Ag!';
        for (let i = 0; i < 12; i++) res += chars.charAt(Math.floor(Math.random() * chars.length));
        return res + '9#';
      })();

      // 3. Create Mailbox in Backend & Stalwart
      const createdMailbox = await apiClient.createMailbox({
        sendingDomainId: targetDomainId,
        localPart: cleanLocalPart,
        displayName: displayName.trim() || undefined,
        password: finalPassword,
        quotaBytes: quotaGigabytes * 1024 * 1024 * 1024,
      });

      let dedicatedUserCreated = false;

      // 4. If requested, create a dedicated UAM employee restricted to ONLY Webmail
      if (createDedicatedUser) {
        try {
          await apiClient.createUamEmployee(currentOrgId, {
            firstName: displayName.trim() || cleanLocalPart,
            lastName: '(Dedicated Mail)',
            email: createdMailbox.emailAddress || fullEmail,
            password: finalPassword,
            role: 'MEMBER',
            screenPermissions: [
              { route: '/webmail', isAllowed: true },
              { route: '/', isAllowed: false },
              { route: '/campaigns', isAllowed: false },
              { route: '/templates', isAllowed: false },
              { route: '/contacts', isAllowed: false },
              { route: '/deliverability', isAllowed: false },
              { route: '/team', isAllowed: false },
              { route: '/billing', isAllowed: false },
              { route: '/api-keys', isAllowed: false },
              { route: '/uam', isAllowed: false },
              { route: '/health', isAllowed: false },
              { route: '/admin', isAllowed: false },
            ],
          });
          dedicatedUserCreated = true;
        } catch (uamErr: any) {
          console.warn('UAM employee creation notice:', uamErr?.message);
        }
      }

      setCreatedResult({
        mailbox: createdMailbox,
        initialPassword: finalPassword,
        dedicatedUserCreated,
      });

      onSuccess(createdMailbox);
    } catch (err: any) {
      setError(err.message || 'Failed to provision mailbox account.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                <span>Create New Business Mailbox</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono border border-slate-700 hidden sm:inline">
                  Stalwart Native
                </span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Provision a dedicated email account with custom domain, secure credentials, and optional Webmail-only access.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-5 flex-1 min-h-0">
          {error && (
            <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {createdResult ? (
            /* Success Summary Screen */
            <div className="space-y-5">
              <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-800/60 text-emerald-300 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-sm text-white">Mailbox Provisioned Successfully!</h3>
                  <p className="text-xs text-emerald-300/90 mt-0.5">
                    Account is live in Stalwart mail engine and ready to send & receive emails immediately.
                  </p>
                </div>
              </div>

              {/* Credentials Box */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
                <div className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Key className="w-3.5 h-3.5 text-brand-400" />
                  <span>Account Credentials</span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900 border border-slate-800/80">
                    <div>
                      <span className="text-[10px] text-slate-500 block uppercase font-mono">Email Address</span>
                      <span className="font-mono text-white font-medium">{createdResult.mailbox.emailAddress}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(createdResult.mailbox.emailAddress, 'email')}
                      className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
                      title="Copy Email"
                    >
                      {copiedKey === 'email' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900 border border-slate-800/80">
                    <div>
                      <span className="text-[10px] text-slate-500 block uppercase font-mono">Mailbox Password</span>
                      <span className="font-mono text-brand-300 font-medium">{createdResult.initialPassword}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(createdResult.initialPassword || '', 'pass')}
                      className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
                      title="Copy Password"
                    >
                      {copiedKey === 'pass' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Dedicated Access Status */}
              {createdResult.dedicatedUserCreated && (
                <div className="p-3.5 rounded-2xl bg-purple-950/30 border border-purple-800/50 text-xs text-purple-300 flex items-center gap-2.5">
                  <UserCheck className="w-4 h-4 text-purple-400 shrink-0" />
                  <span>
                    <strong>Dedicated Webmail Login Created:</strong> User can sign in using this email and password, and will strictly only see the Webmail Client.
                  </span>
                </div>
              )}

              {/* Server Connection Parameters */}
              <div className="p-3.5 rounded-2xl bg-slate-950/60 border border-slate-800/80 text-[11px] text-slate-400 space-y-1.5 font-mono">
                <div className="font-sans font-bold text-slate-300 text-xs mb-1">Direct IMAP & SMTP Connection</div>
                <div className="flex justify-between">
                  <span>IMAP Host / SSL Port:</span>
                  <span className="text-white">app-staging.axiino.com:993 (SSL)</span>
                </div>
                <div className="flex justify-between">
                  <span>SMTP Outbound / Port:</span>
                  <span className="text-white">app-staging.axiino.com:587 / :25</span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold text-xs transition shadow-lg shadow-brand-500/20"
                >
                  Done & Go to Webmail
                </button>
              </div>
            </div>
          ) : (
            /* Creation Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Domain Selection */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-blue-400" />
                    <span>Sending Domain</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddingNewDomain(!isAddingNewDomain)}
                    className="text-[11px] text-brand-400 hover:text-brand-300 underline"
                  >
                    {isAddingNewDomain ? '← Select Existing Domain' : '+ Configure New Domain'}
                  </button>
                </div>

                {isAddingNewDomain ? (
                  <div className="space-y-1.5">
                    <input
                      type="text"
                      value={customDomainName}
                      onChange={(e) => setCustomDomainName(e.target.value)}
                      placeholder="e.g. axiino.com or yourbrand.com"
                      required
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
                    />
                    <p className="text-[10px] text-slate-500">
                      Domain will be registered in your workspace and provisioned in Stalwart.
                    </p>
                  </div>
                ) : (
                  <div>
                    {isLoadingDomains ? (
                      <div className="p-2 text-xs text-slate-400 flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Loading verified domains...</span>
                      </div>
                    ) : domains.length === 0 ? (
                      <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-400">
                        <span>No domains registered yet. Click &quot;Configure New Domain&quot; above.</span>
                      </div>
                    ) : (
                      <select
                        value={selectedDomainId}
                        onChange={(e) => setSelectedDomainId(e.target.value)}
                        className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                      >
                        {domains.map((d) => (
                          <option key={d.id} value={d.id}>
                            @{d.domain} ({d.status})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>

              {/* Email Local Part & Display Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Username / Local Part
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={localPart}
                      onChange={(e) => setLocalPart(e.target.value)}
                      placeholder="e.g. support, sales"
                      required
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    Sender Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g. Support Team"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              {/* Email Preview */}
              {fullEmail && (
                <div className="p-2.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Resulting Mailbox:</span>
                  <span className="font-mono text-emerald-400 font-semibold">{fullEmail}</span>
                </div>
              )}

              {/* Password Setting */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-amber-400" />
                    <span>Mailbox Password</span>
                  </label>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-[11px] text-brand-400 hover:text-brand-300 flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Generate Strong Password</span>
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Set custom password (or click generate)"
                    className="w-full px-3.5 py-2 pr-10 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Leave blank to auto-generate a secure password upon creation.
                </p>
              </div>

              {/* Quota Setting */}
              <div>
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5 mb-1.5">
                  <HardDrive className="w-3.5 h-3.5 text-purple-400" />
                  <span>Storage Quota</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 25, 50].map((gb) => (
                    <button
                      key={gb}
                      type="button"
                      onClick={() => setQuotaGigabytes(gb)}
                      className={`py-1.5 rounded-xl text-xs font-semibold border transition ${
                        quotaGigabytes === gb
                          ? 'bg-brand-600 text-white border-brand-500 shadow-sm'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      {gb} GB
                    </button>
                  ))}
                </div>
              </div>

              {/* Dedicated Webmail User Access Option */}
              <div className="p-3.5 rounded-2xl bg-brand-950/20 border border-brand-800/40 space-y-2">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createDedicatedUser}
                    onChange={(e) => setCreateDedicatedUser(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded bg-slate-900 border-slate-700 text-brand-600 focus:ring-brand-500"
                  />
                  <div>
                    <span className="text-xs font-bold text-white block">
                      Enable Dedicated Webmail Login for this User
                    </span>
                    <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                      Creates a platform account with access <strong className="text-brand-300">strictly restricted to Webmail</strong>. All other 11 screens (Campaigns, Billing, UAM, Diagnostics, Admin) are completely locked & hidden.
                    </p>
                  </div>
                </label>
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800 sticky bottom-0 bg-slate-900/95 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-semibold text-xs transition flex items-center gap-2 shadow-lg shadow-brand-500/20 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Provisioning in Stalwart...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-3.5 h-3.5" />
                      <span>Create Mailbox Account</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
